import { useEffect } from 'react';
import { useResponsive } from '../hooks/useResponsive';
import { Button } from './Button';
import { TextInput } from './TextInput';
import { setAddingTextStatus } from '../../../redux-tool/slice/toolSlice';
import { useDispatch } from 'react-redux';

/**
 * Enhanced floating text input modal for tablet devices
 * Features larger dimensions, backdrop blur, enhanced styling, and close button
 */
export const EnhancedTextModal = ({
  text,
  setText,
  showFloatingInput,
  setShowFloatingInput,
  activeTextId,
  isSubmitting,
  setTextList,
  findTextByIdMemo,
  handleSubmit,
  handleUpdateText,
  focusInput,
  inputRef,
  setShouldMaintainFocus,
}) => {
  // Get responsive info
  const { isMobile, isTablet } = useResponsive();
  const dispatch = useDispatch();

  // Simple update function - update text content directly in current logo
  const updateTextContent = (value) => {
    if (activeTextId && value.trim()) {
      // Find the text item and update it directly
      const logo = findTextByIdMemo(activeTextId);
      if (logo) {
        // Update the current logo object directly
        logo.Text = value.trim();
        // Force re-render by updating textList state
        setTextList((prev) => prev.map((item) => (item.Id === activeTextId ? logo : item)));
      }
    }
  };

  useEffect(() => {
    if (showFloatingInput) {
      dispatch(setAddingTextStatus(true));
    } else {
      dispatch(setAddingTextStatus(false));
    }
  }, [showFloatingInput]);

  if (!showFloatingInput) return null;

  return (
    <>
      <style>
        {`
          /* Tablet Text Modal Styles - Full Screen Dialog */
          .tablet-text-modal {
            position: fixed !important;
            top: 0 !important;
            left: 0 !important;
            right: 0 !important;
            bottom: 0 !important;
            width: 100vw !important;
            height: 100vh !important;
            background: rgba(0, 0, 0, 0.95) !important;
            backdrop-filter: blur(10px) !important;
            -webkit-backdrop-filter: blur(10px) !important;
            z-index: 1000 !important;
            pointer-events: auto !important;
            display: flex !important;
            flex-direction: column !important;
            align-items: center !important;
            justify-content: center !important;
            max-width: none !important;
            min-width: none !important;
          }
          
          /* Ensure input is properly styled on tablet */
          .tablet-text-modal input,
          .tablet-text-modal textarea {
            font-size: 18px !important;
            -webkit-appearance: none !important;
            border-radius: 0 !important;
            background: transparent !important;
            color: #fff !important;
          }
          
          /* Prevent zoom on input focus for tablet */
          .tablet-text-modal input:focus,
          .tablet-text-modal textarea:focus {
            font-size: 18px !important;
            transform: none !important;
          }
        `}
      </style>
      <div className="tablet-text-modal">
        {/* Top-right action buttons (Apply + Close) */}
        <div
          style={{
            position: 'absolute',
            top: '20px',
            right: '20px',
            display: 'flex',
            gap: '12px',
            zIndex: 1001,
          }}
        >
          {text.trim() ? (
            <Button
              onClick={() => {
                if (text.trim() && !isSubmitting) {
                  if (!activeTextId || activeTextId === '') {
                    handleSubmit();
                  } else {
                    handleUpdateText();
                  }
                  // Close the overlay
                  setShowFloatingInput(false);
                }
              }}
              disabled={!text.trim() || isSubmitting}
              style={{
                padding: '12px',
                borderRadius: '12px',
                fontSize: '20px',
                fontWeight: '600',
                minWidth: '56px',
                minHeight: '32px',
                width: '56px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: text.trim() && !isSubmitting ? 1 : 0.5,
                background:
                  text.trim() && !isSubmitting
                    ? `linear-gradient(135deg, #2BFFFF 0%, #40E0FF 100%)`
                    : 'rgba(255,255,255,0.1)',
                border: text.trim() && !isSubmitting ? 'none' : `1px solid rgba(255,255,255,0.2)`,
                cursor: text.trim() && !isSubmitting ? 'pointer' : 'not-allowed',
                touchAction: 'manipulation',
                WebkitTapHighlightColor: 'transparent',
                boxShadow:
                  text.trim() && !isSubmitting ? `0 8px 24px rgba(43, 255, 255, 0.4)` : 'none',
                transition: 'all 0.3s ease',
                transform: text.trim() && !isSubmitting ? 'scale(1)' : 'scale(0.95)',
                color: text.trim() && !isSubmitting ? '#000' : 'rgba(255,255,255,0.6)',
              }}
            >
              {isSubmitting ? (
                <div
                  style={{
                    width: '20px',
                    height: '20px',
                    border: '2px solid currentColor',
                    borderTop: '2px solid transparent',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                  }}
                />
              ) : (
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{ display: 'block', color: 'white' }}
                >
                  <path d="M20 6L9 17l-5-5" />
                </svg>
              )}
            </Button>
          ) : (
            <Button
              variant="secondary"
              onClick={() => setShowFloatingInput(false)}
              style={{
                padding: '12px',
                borderRadius: '12px',
                fontSize: '20px',
                fontWeight: '600',
                minWidth: '56px',
                minHeight: '32px',
                width: '56px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'rgba(255,255,255,0.1)',
                border: `1px solid rgba(255,255,255,0.2)`,
                color: 'rgba(255,255,255,0.9)',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
              }}
              aria-label="Close text modal"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ display: 'block' }}
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </Button>
          )}
        </div>

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            minWidth: '320px',
            maxWidth: '600px',
            width: '90%',
          }}
        >
          {/* Text Input */}
          <div
            onClick={() => focusInput()}
            style={{
              background: 'rgba(0, 0, 0, 0.3)',
              borderRadius: '12px',
              padding: '5px',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = 'rgba(43, 255, 255, 0.5)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = 'rgba(43, 255, 255, 0.3)';
            }}
          >
            <TextInput
              value={text}
              placeholder="Enter text here"
              multiline={true}
              onChange={(e) => {
                setText(e.target.value);
                // Update text in 3D scene as user types (real-time)
                updateTextContent(e.target.value);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  // Desktop: Shift+Enter for new line, Enter to submit
                  // Mobile/Tablet: Enter for new line, no submit on Enter
                  if (isMobile || isTablet) {
                    // Mobile/Tablet: Enter creates new line
                    e.preventDefault();
                    setText(text + '\n');
                  } else {
                    // Desktop: Shift+Enter for new line, Enter to submit
                    if (e.shiftKey) {
                      // Shift+Enter: Add new line
                      e.preventDefault();
                      setText(text + '\n');
                    } else {
                      // Enter: Submit
                      e.preventDefault();
                      e.stopPropagation();
                      if (text.trim() && !isSubmitting) {
                        if (!activeTextId || activeTextId === '') {
                          handleSubmit();
                        } else {
                          handleUpdateText();
                        }
                        setShowFloatingInput(false);
                      }
                    }
                  }
                }
              }}
              onBlur={(e) => {
                if (text.trim() && !e.currentTarget.contains(e.relatedTarget)) {
                  const target = e.relatedTarget;
                  if (
                    target &&
                    (target.closest('.MuiSlider-root') ||
                      target.closest('.color-swatch') ||
                      target.closest('.font-button') ||
                      target.closest('.clickable'))
                  ) {
                    setShouldMaintainFocus(true);
                    return;
                  }
                }
              }}
              onFocus={() => {
                if (inputRef.current) {
                  inputRef.current.focus();
                }
              }}
              onTouchStart={(e) => {
                e.stopPropagation();
                if (inputRef.current) {
                  inputRef.current.focus();
                }
              }}
              ref={inputRef}
              style={{
                width: '100%',
                minHeight: '60px',
                fontSize: '14px',
                lineHeight: '1.5',
                padding: '0',
                background: 'transparent',
                border: 'none',
                color: '#FFFFFF',
                fontFamily: 'inherit',
                resize: 'none',
              }}
            />
          </div>

          {/* Apply button moved to top-right; no button here anymore */}
        </div>
      </div>
    </>
  );
};
